import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useAlert } from '../hooks/useAlert';
import { useToast } from '../context/ToastContext';
import { useKyc, kycLinkFromError } from '../context/KycContext';
import { SkeletonCard } from '../components/Skeleton';
import { PageHeader, SectionLabel, TextField } from '../components/Form';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Icon from '../components/Icon';

interface PayoutCard {
  refId: string;
  last4: string | null;
  isDefault: boolean;
  expiryDate: string | null;
  type: string;            // 'card' | 'venmo'
  username: string | null;
  cardHolderName: string | null;
}

type AddMode = 'card' | 'venmo' | null;

export default function PayoutMethods() {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { alert } = useAlert();
  const toast = useToast();
  const { mustVerify, openKyc, refresh: refreshKyc } = useKyc();

  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);
  const [cards, setCards] = useState<PayoutCard[]>([]);
  const [busy, setBusy] = useState(false);
  const [addMode, setAddMode] = useState<AddMode>(null);

  // Add-card form
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [holder, setHolder] = useState('');
  const [zip, setZip] = useState('');
  const [venmo, setVenmo] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ hasProfile: boolean; cards: PayoutCard[] }>('/orders/payout-methods');
      setHasProfile(!!res.data.hasProfile);
      setCards(res.data.cards || []);
    } catch {
      alert(t('profile.payout.title'), t('profile.payout.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [alert, t]);

  useEffect(() => { load(); }, [load]);

  // Turn a failed Solar call into a user message. VERIFY_KYC re-opens the
  // hosted verification; everything else shows the server's message.
  const handleError = async (err: any) => {
    if (kycLinkFromError(err) !== null || err?.response?.data?.code === 'VERIFY_KYC') {
      await refreshKyc();
      alert(t('profile.payout.title'), t('profile.payout.verifyFirst'));
      return;
    }
    alert(t('profile.payout.title'), err?.response?.data?.message || t('profile.payout.actionFailed'));
  };

  const resetForm = () => {
    setCardNumber(''); setExpiry(''); setHolder(''); setZip(''); setVenmo('');
    setAddMode(null);
  };

  const submitAdd = async () => {
    setBusy(true);
    try {
      if (addMode === 'card') {
        await api.post('/orders/payout-methods/card', {
          cardNumber, expiryDate: expiry.trim(), cardHolderName: holder.trim(), zipCode: zip.trim(),
        });
      } else {
        await api.post('/orders/payout-methods/venmo', { username: venmo.trim() });
      }
      toast.success(t('profile.payout.toast.added'));
      resetForm();
      await load();
    } catch (err) {
      await handleError(err);
    } finally {
      setBusy(false);
    }
  };

  const setDefault = async (refId: string) => {
    setBusy(true);
    try {
      await api.post('/orders/payout-methods/default', { refId });
      toast.success(t('profile.payout.toast.defaultSet'));
      await load();
    } catch (err) {
      await handleError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = (refId: string) => {
    alert(t('profile.payout.confirmDeleteTitle'), t('profile.payout.confirmDeleteMessage'), [
      { text: t('profile.payout.form.cancel'), style: 'cancel' },
      {
        text: t('profile.payout.delete'),
        style: 'destructive',
        onPress: async () => {
          setBusy(true);
          try {
            await api.post('/orders/payout-methods/delete', { refId });
            toast.success(t('profile.payout.toast.removed'));
            await load();
          } catch (err) {
            await handleError(err);
          } finally {
            setBusy(false);
          }
        },
      },
    ]);
  };

  const canSubmit = addMode === 'card'
    ? cardNumber.trim() && expiry.trim() && holder.trim() && zip.trim()
    : !!venmo.trim();

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <PageHeader icon="credit-card" title={t('profile.payout.title')} />

      {loading ? (
        <div style={{ padding: 16 }}><SkeletonCard /><SkeletonCard /></div>
      ) : (
        <div style={{ padding: 16, paddingBottom: 40 }}>
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            backgroundColor: colors.accentSoft, borderRadius: 12, padding: 12, marginBottom: 16,
          }}>
            <Icon name="info-circle" size={14} color={colors.accentText} style={{ marginTop: 2 }} />
            <div style={{ flex: 1, fontSize: 14, lineHeight: 1.5, fontWeight: 600, color: colors.accentText }}>
              {t('profile.payout.intro')}
            </div>
          </div>

          {!hasProfile ? (
            <div>
              <EmptyNote icon="university" text={t('profile.payout.noProfile')} />
              <Button label={t('profile.payout.addFundsFirst')} onClick={() => navigate('/wallet-topup')} style={{ marginTop: 12 }} />
            </div>
          ) : mustVerify ? (
            <div style={{
              backgroundColor: colors.dangerSoft, border: `1px solid ${colors.danger}`,
              borderRadius: 14, padding: 16, textAlign: 'center',
            }}>
              <Icon name="exclamation-circle" size={20} color={colors.danger} />
              <div style={{ fontSize: 14, fontWeight: 700, color: colors.danger, margin: '10px 0 12px' }}>
                {t('profile.payout.verifyFirst')}
              </div>
              <Button label={t('profile.payout.verifyCta')} onClick={openKyc} />
            </div>
          ) : (
            <>
              <SectionLabel>{t('profile.payout.savedTitle')}</SectionLabel>
              {cards.length === 0 ? (
                <EmptyNote icon="credit-card" text={t('profile.payout.empty')} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {cards.map(c => (
                    <CardRow
                      key={c.refId}
                      card={c}
                      busy={busy}
                      onSetDefault={() => setDefault(c.refId)}
                      onRemove={() => remove(c.refId)}
                    />
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
                <AddButton icon="credit-card" label={t('profile.payout.addCard')} onClick={() => setAddMode('card')} />
                <AddButton icon="mobile-alt" label={t('profile.payout.addVenmo')} onClick={() => setAddMode('venmo')} />
              </div>
            </>
          )}
        </div>
      )}

      <Modal open={addMode !== null} onClose={resetForm}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 800, color: colors.text, marginBottom: 16 }}>
          {addMode === 'venmo' ? t('profile.payout.form.venmoTitle') : t('profile.payout.form.cardTitle')}
        </div>

        {addMode === 'card' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field label={t('profile.payout.form.cardNumber')}>
              <TextField icon="credit-card" value={cardNumber} onChange={setCardNumber} placeholder={t('profile.payout.form.cardNumberPlaceholder')} inputMode="numeric" autoComplete="cc-number" />
            </Field>
            <Field label={t('profile.payout.form.expiry')}>
              <TextField icon="calendar" value={expiry} onChange={setExpiry} placeholder={t('profile.payout.form.expiryPlaceholder')} autoComplete="cc-exp" />
            </Field>
            <Field label={t('profile.payout.form.holder')}>
              <TextField icon="user" value={holder} onChange={setHolder} placeholder={t('profile.payout.form.holderPlaceholder')} autoComplete="cc-name" />
            </Field>
            <Field label={t('profile.payout.form.zip')}>
              <TextField icon="map-marker-alt" value={zip} onChange={setZip} placeholder={t('profile.payout.form.zipPlaceholder')} inputMode="numeric" autoComplete="postal-code" />
            </Field>
          </div>
        ) : (
          <Field label={t('profile.payout.form.venmoUsername')}>
            <TextField icon="at" value={venmo} onChange={setVenmo} placeholder={t('profile.payout.form.venmoPlaceholder')} autoCapitalize="none" />
          </Field>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <Button label={t('profile.payout.form.cancel')} variant="outline" onClick={resetForm} />
          <Button label={t('profile.payout.form.submit')} loading={busy} disabled={!canSubmit} onClick={submitAdd} />
        </div>
      </Modal>
    </div>
  );
}

function EmptyNote({ icon, text }: { icon: string; text: string }) {
  const { colors } = useTheme();
  return (
    <div style={{
      backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
      borderRadius: 14, padding: '28px 20px', textAlign: 'center',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
    }}>
      <Icon name={icon} size={26} color={colors.textMuted} />
      <div style={{ fontSize: 14, color: colors.textSub, lineHeight: 1.5, maxWidth: 320 }}>{text}</div>
    </div>
  );
}

function CardRow({
  card, busy, onSetDefault, onRemove,
}: {
  card: PayoutCard; busy: boolean; onSetDefault: () => void; onRemove: () => void;
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const isVenmo = card.type === 'venmo';
  const title = isVenmo
    ? t('profile.payout.venmoLabel', { username: card.username || '' })
    : t('profile.payout.cardLabel', { last4: card.last4 || '••••' });

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: 14,
      backgroundColor: colors.card, border: `1px solid ${card.isDefault ? colors.accent : colors.cardBorder}`,
      borderRadius: 14,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        backgroundColor: colors.accentSoft,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={isVenmo ? 'mobile-alt' : 'credit-card'} size={16} color={colors.accentText} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{title}</div>
        {card.expiryDate && (
          <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 2 }}>
            {t('profile.payout.expires', { expiry: card.expiryDate })}
          </div>
        )}
        {card.isDefault ? (
          <span style={{
            display: 'inline-block', marginTop: 6, padding: '2px 8px', borderRadius: 999,
            backgroundColor: colors.accentSoft, color: colors.accentText, fontSize: 14, fontWeight: 800,
          }}>
            {t('profile.payout.default')}
          </span>
        ) : (
          <button
            disabled={busy}
            onClick={onSetDefault}
            style={{
              marginTop: 6, background: 'transparent', border: 'none', padding: 0,
              color: colors.accentText, fontSize: 14, fontWeight: 700,
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            {t('profile.payout.setDefault')}
          </button>
        )}
      </div>
      <button
        aria-label={t('profile.payout.delete')}
        disabled={busy}
        onClick={onRemove}
        style={{
          width: 40, height: 40, borderRadius: 20, flexShrink: 0,
          background: 'transparent', border: 'none', cursor: busy ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon name="trash-alt" size={15} color={colors.textMuted} />
      </button>
    </div>
  );
}

function AddButton({ icon, label, onClick }: { icon: string; label: string; onClick: () => void }) {
  const { colors } = useTheme();
  return (
    <button
      onClick={onClick}
      className="sls-press"
      style={{
        flex: '1 1 140px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '14px 0', borderRadius: 12, minHeight: 50,
        backgroundColor: colors.card, border: `1.5px dashed ${colors.cardBorder}`,
        color: colors.text, fontSize: 14, fontWeight: 700, cursor: 'pointer',
      }}
    >
      <Icon name={icon} size={14} color={colors.accentText} />
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, color: colors.textSub, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}
